import { ApolloError, FetchResult } from "@apollo/client";
import { useCallback } from "react";
import {
  UpdateCartData,
  UpdateCartProps,
  useUpdateCart,
} from "src/hooks/useUpdateCart";
import { useSetLineItemSupplyChannel } from "src/hooks/useSetLineItemSupplyChannel";
import { LineItem } from "src/types/ctgraphql.d";
import { CartInfo } from "src/types/cartInfo";
import { getCartInfo } from "src/utils/ct/cart-helper";
import { useUpdateCartContext } from "./cart/use-update-cart-context";

interface IncreaseCartItemHook {
  data?: UpdateCartData;
  loading: boolean;
  error?: ApolloError;
  increaseCartItem: (
    sku: string,
    item: LineItem | undefined,
    cartInfo: CartInfo
  ) => Promise<FetchResult<UpdateCartData> | undefined>;
}

function useIncreaseCartItem({
  refetchQueries = [],
  onCompleted,
}: UpdateCartProps = {}): IncreaseCartItemHook {
  const updateCartContext = useUpdateCartContext();
  const { setLineItemSupplyChannel } = useSetLineItemSupplyChannel({
    refetchQueries: ["cart"],
  });

  const { updateCart, data, loading, error } = useUpdateCart({
    refetchQueries,
    onCompleted: (updateMyCartData) => {
      const item = updateMyCartData.updateMyCart?.lineItems.find(
        ({ supplyChannel }) => !supplyChannel
      );

      if (item?.variant?.availability?.channels?.results?.[0]?.channel?.id) {
        setLineItemSupplyChannel(
          item.id,
          item.variant?.availability?.channels?.results?.[0]?.channel.id,
          getCartInfo(updateMyCartData.updateMyCart)
        );
      }

      if (onCompleted) {
        onCompleted(updateMyCartData);
      }

      updateCartContext(updateMyCartData?.updateMyCart);
    },
  });

  const increaseCartItem: IncreaseCartItemHook["increaseCartItem"] =
    useCallback(
      async (sku, item, cartInfo) => {
        return await updateCart({
          cartInfo,
          actions: [
            {
              addLineItem: {
                sku,
                ...(item?.supplyChannel?.id
                  ? {
                      supplyChannel: {
                        id: item.supplyChannel.id,
                      },
                    }
                  : {}),
              },
            },
          ],
        });
      },
      [updateCart]
    );

  return { increaseCartItem, data, loading, error };
}

export { useIncreaseCartItem };
